import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { useSelector } from "react-redux";
import axios from "axios";
import EntityFormV2View from "./EntityFormV2View";

const isValidUUID = (uuid) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid);

const SITE_FORM_CONFIG = {
  pageTitle: "Add New Site",
  pageSubtitle: "Create and configure a new site for monitoring",
  nameLabel: "Site Name",
  idLabel: "Site Id",
  showMachineType: false,
  showLocation: true,
  makeRequired: false,
};

const CreateSiteV2 = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const { groups } = useSelector((state) => state.groupSlice);
  const { locations, makes, categories } = useSelector((state) => state.tagsSlice);
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
    accept: {
      "image/*": [".jpeg", ".png"],
    },
    multiple: false,
  });

  const [search, setSearch] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  const [make, setMake] = useState(null);
  const [location, setLocation] = useState(null);
  const [machineId, setMachineId] = useState("");
  const [machineName, setMachineName] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadedIcon, setUploadedIcon] = useState(null);
  const [machineImage, setMachineImage] = useState(null);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [ParentGroupId, setParentGroupId] = useState(null);
  const [userList, setUserList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showList, setShowList] = useState(false);
  const [emails, setEmails] = useState([]);
  const [tagsData, setTagsData] = useState([]);

  useEffect(() => {
    setUserList(userData.channelUsers);
  }, [userData.channelUsers]);

  useEffect(() => {
    let taggedUser = emails.map((e) => e.uuid);
    let selectedUsers = userData.channelUsers.filter((user) => taggedUser.includes(user.uuid));
    let unSelectedUsers = userData.channelUsers.filter((user) => !taggedUser.includes(user.uuid));
    setSelectedUsers(selectedUsers);
    setUserList(unSelectedUsers);
  }, [userData?.channelUsers, emails]);

  useEffect(() => {
    const makeTag = tagsData.find((tag) => tag.categoryId === categories.make);
    const locationTag = tagsData.find((tag) => tag.categoryId === categories.location);

    setMake(makeTag ? { value: makeTag.name, label: makeTag.name, id: makeTag.id } : null);
    setLocation(locationTag ? { value: locationTag.name, label: locationTag.name, id: locationTag.id } : null);
  }, [tagsData, categories]);

  const fetchMachineDetails = useCallback(
    async (type) => {
      try {
        const { data } = await axios.get(`${configData.config.API_URL}/api/machine/details/${type}`, {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        });

        let groupIds = data.data.groups;

        const { name, data: machineData, tags, email } = data.data;

        let taggedUser = email.map((e) => e.uuid);
        let selectedUsers = userData.channelUsers.filter((user) => taggedUser.includes(user.uuid));
        let unSelectedUsers = userData.channelUsers.filter((user) => !taggedUser.includes(user.uuid));
        setSelectedUsers(selectedUsers);
        setUserList(unSelectedUsers);
        setEmails(email || []);
        setTagsData(tags || []);
        setMachineName(name);
        setMachineId(machineData.machineId);
        setSerialNumber(machineData.serialNo);
        setMachineImage(data.data.thumbnail || null);
        if (data.data.groups) {
          const groupResponse = await axios.post(
            `${configData.config.UNO_URL}/api/user/bulk/group/details`,
            { groupIds: groupIds },
            {
              headers: {
                Authorization: `Bearer ${userData.token}`,
                "Content-Type": "application/json",
              },
            }
          );

          let filterParentGroup = groupResponse.data.data.filter((e) => e.uuid !== userData.user.group.uuid);

          setSelectedGroups(filterParentGroup);
        }

        const makeTag = tags.find((tag) => tag.categoryId === categories.make);
        const locationTag = tags.find((tag) => tag.categoryId === categories.location);

        setMake(makeTag ? { value: makeTag.name, label: makeTag.name, id: makeTag.id } : null);
        setLocation(locationTag ? { value: locationTag.name, label: locationTag.name, id: locationTag.id } : null);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching machine details:", error);
        setLoading(false);
      }
    },
    [categories.make, categories.location, configData.config.API_URL, userData.token]
  );

  const handleChange = (setter) => (e) => {
    setError((prevError) => ({ ...prevError, [e.target.name]: null }));
    setter(e.target.value);
  };

  const handleSelectChange = (setter) => (option) => {
    setError((prevError) => ({ ...prevError, [option?.name]: null }));
    setter(option);
  };

  useEffect(() => {
    if (acceptedFiles.length) {
      const file = acceptedFiles[0];
      setUploadFile(file);

      const reader = new FileReader();
      reader.onload = () => setUploadedIcon(reader.result);
      reader.readAsDataURL(file);
    }
  }, [acceptedFiles]);

  const handleCancelUpload = (event) => {
    event.stopPropagation();
    setUploadedIcon(null);
    setMachineImage(null);
  };

  const handleSave = async () => {
    const errors = {
      machineName: machineName ? null : "Machine name is required",
      machineId: machineId ? null : "Model number is required",
    };

    if (Object.values(errors).some((error) => error !== null)) {
      setError(errors);
      return;
    }

    setLoading(true);
    const tags = [make?.id, location?.id].filter(Boolean);

    const machineData = {
      name: machineName,
      machineId,
      serialNo: serialNumber,
      tags: tags.length > 0 ? tags : [],
      groups: selectedGroups.map((e) => e.uuid),
      users: selectedUsers.map((e) => e.uuid),
      isSite: true,
    };

    machineData.groups.push(ParentGroupId);

    try {
      if (isValidUUID(type)) {
        await updateMachine(machineData, uploadFile, type);
      } else {
        await createMachine(machineData, uploadFile);
      }
      resetForm();
    } catch (error) {
      console.error("Error saving machine:", error);
      setLoading(false);
    }
  };

  const updateMachine = async (machineData, file, uuid) => {
    setProcessing(true);
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
      const { data } = await axios.post(
        `${configData.config.UNO_URL}/api/resource/machine-monitoring/attachment/${uuid}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      machineData.thumbnail = data.url;
    }

    await axios.put(`${configData.config.API_URL}/api/site/main/update/${uuid}`, machineData, {
      headers: {
        Authorization: `Bearer ${userData.token}`,
        "Content-Type": "application/json",
      },
    });

    setProcessing(false);
    navigate(`/site-info/${type}`);
  };

  const createMachine = async (machineData, file) => {
    setProcessing(true);

    const { data: responseData } = await axios.post(`${configData.config.API_URL}/api/site/main/create`, machineData, {
      headers: {
        Authorization: `Bearer ${userData.token}`,
        "Content-Type": "application/json",
      },
    });

    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      const { data: fileData } = await axios.post(
        `${configData.config.UNO_URL}/api/resource/machine-monitoring/attachment/${responseData.machineId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      machineData.thumbnail = fileData.url;

      await axios.put(`${configData.config.API_URL}/api/site/main/update/${responseData.machineId}`, machineData, {
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "Content-Type": "application/json",
        },
      });
    }

    setProcessing(false);
    navigate(`/site-list/view/${userData?.user?.group?.uuid}`);
  };

  const resetForm = () => {
    setMake(null);
    setLocation(null);
    setMachineId("");
    setMachineName("");
    setSerialNumber("");
    setMachineImage(null);
    setUploadedIcon(null);
    setSelectedGroups([]);
    setError({});
  };

  useEffect(() => {
    if (isValidUUID(type)) {
      fetchMachineDetails(type);
    } else {
      setLoading(false);
    }
  }, [type, fetchMachineDetails]);

  const handleClickSelectUSer = (user, index) => {
    let _userList = [...userList];
    _userList.splice(index, 1);
    setUserList(_userList);
    setSelectedUsers((pre) => [...pre, user]);
    setNameSearch("");
  };
  const handleClickCancelUSer = (user, index) => {
    let _userList = [...userList, user];
    setUserList(_userList);
    let _selectedUsers = [...selectedUsers];
    _selectedUsers.splice(index, 1);
    setSelectedUsers(_selectedUsers);
  };

  return (
    <EntityFormV2View
      config={SITE_FORM_CONFIG}
      navigate={navigate}
      getRootProps={getRootProps}
      getInputProps={getInputProps}
      uploadedIcon={uploadedIcon}
      machineImage={machineImage}
      handleCancelUpload={handleCancelUpload}
      machineName={machineName}
      machineId={machineId}
      serialNumber={serialNumber}
      error={error}
      handleChange={handleChange}
      setMachineName={setMachineName}
      setMachineId={setMachineId}
      setSerialNumber={setSerialNumber}
      setMake={setMake}
      setLocation={setLocation}
      setMachineType={() => {}}
      make={make}
      location={location}
      machineType={null}
      makes={makes}
      locations={locations}
      categories={categories}
      handleSelectChange={handleSelectChange}
      selectedUsers={selectedUsers}
      userList={userList}
      nameSearch={nameSearch}
      setNameSearch={setNameSearch}
      showList={showList}
      setShowList={setShowList}
      handleClickSelectUSer={handleClickSelectUSer}
      handleClickCancelUSer={handleClickCancelUSer}
      search={search}
      setSearch={setSearch}
      groups={groups}
      selectedGroups={selectedGroups}
      setSelectedGroups={setSelectedGroups}
      setParentGroupId={setParentGroupId}
      ParentGroupId={ParentGroupId}
      processing={processing}
      handleSave={handleSave}
      resetForm={resetForm}
    />
  );
};

export default CreateSiteV2;
